import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { expendituresService } from "../services/expendituresService";
import s3Service from "../services/s3Service";
import styles from "../assets/expenseAccountCreate.module.css";

const ExpenseAccountCreate = () => {
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [expenses, setExpenses] = useState([
    {
      ticket_id: "",
      property_id: "",
      property_name: "",
      type_id: "",
      purchase_order_number: "",
      invoice_number: "",
      creditor: "",
      due_date: "",
      amount: "",
      file: null,
      fileKey: null, // S3 key for uploaded file
      fileUploading: false,
      fileProgress: 0,
    },
  ]);

  // Dropdown options
  const [types, setTypes] = useState([
    { id: 1, name: "Maintenance" },
    { id: 2, name: "Utilities" },
    { id: 3, name: "Insurance" },
    { id: 4, name: "Repairs" },
    { id: 5, name: "Cleaning" },
  ]);

  const [properties, setProperties] = useState([]);
  const [propertySearchTerm, setPropertySearchTerm] = useState("");
  const [showPropertyDropdown, setShowPropertyDropdown] = useState(false);


  // Property search functionality
  useEffect(() => {
    const fetchProperties = async () => {
      console.log("🔍 [FRONTEND] Property search term:", propertySearchTerm, "Length:", propertySearchTerm.length);
      if (propertySearchTerm.length >= 1 || propertySearchTerm === "*") {
        console.log("🔍 [FRONTEND] Making API call for property search:", propertySearchTerm);
        try {
          const propertiesData = await expendituresService.searchProperties(propertySearchTerm);
          console.log("🔍 [FRONTEND] API response:", propertiesData);
          if (propertiesData && Array.isArray(propertiesData)) {
            setProperties(propertiesData);
            setShowPropertyDropdown(true);
          } else {
            setProperties([]);
            setShowPropertyDropdown(false);
          }
        } catch (error) {
          console.error("❌ [FRONTEND] Error searching properties:", error);
          setProperties([]);
          setShowPropertyDropdown(false);
        }
      } else {
        setProperties([]);
        setShowPropertyDropdown(false);
      }
    };

    fetchProperties();
  }, [propertySearchTerm]);

  // Property search handlers
  const handlePropertySearch = (e) => {
    const value = e.target.value;
    setPropertySearchTerm(value);
    setShowPropertyDropdown(value.length > 0);
  };

  const handlePropertySelect = (property, index) => {
    const updatedExpenses = [...expenses];
    updatedExpenses[index].property_name = property.name;
    updatedExpenses[index].property_id = property.id;
    setExpenses(updatedExpenses);
    setPropertySearchTerm("");
    setShowPropertyDropdown(false);
  };

  const loadData = useCallback(async () => {
    try {
      // Load from API
      try {
        const typesResponse = await expendituresService.getTypes();

        setTypes(typesResponse.data || []);
        // setProperties(propertiesResponse.data || []); // Uncomment when API is ready
      } catch (apiError) {
        console.error("API call failed:", apiError);
        toast.error("Failed to load form data. Please try again.");
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load data");
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Add new expense row
  const addExpense = () => {
    setExpenses((prev) => [
      ...prev,
      {
        ticket_id: "",
        property_id: "",
        property_name: "",
        type_id: "",
        purchase_order_number: "",
        invoice_number: "",
        creditor: "",
        due_date: "",
        amount: "",
        file: null,
        fileKey: null,
        fileUploading: false,
        fileProgress: 0,
      },
    ]);
  };

  // Remove expense row
  const removeExpense = (index) => {
    if (expenses.length > 1) {
      setExpenses((prev) => prev.filter((_, i) => i !== index));
    }
  };

  // Duplicate expense row
  const duplicateExpense = (index) => {
    const expenseToDuplicate = expenses[index];
    setExpenses((prev) => [
      ...prev,
      {
        ...expenseToDuplicate,
        invoice_number: "", // Clear invoice number for new expense
        file: null, // Clear file for new expense
        fileKey: null,
        fileUploading: false,
        fileProgress: 0,
      },
    ]);
  };

  // Handle input changes
  const handleInputChange = (index, field, value) => {
    setExpenses((prev) =>
      prev.map((expense, i) =>
        i === index ? { ...expense, [field]: value } : expense
      )
    );

    // Clear error for this field
    if (errors[`expenses.${index}.${field}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`expenses.${index}.${field}`];
        return newErrors;
      });
    }
  };


  // Handle file change with S3 upload
  const handleFileChange = async (index, file) => {
    if (!file) return;

    // Update state to show uploading
    setExpenses((prev) =>
      prev.map((expense, i) =>
        i === index
          ? {
              ...expense,
              file,
              fileUploading: true,
              fileProgress: 0,
            }
          : expense
      )
    );

    try {
      // Upload file to S3
      // Laravel path structure: public/expenditures/files/{filename}
      // Actual S3 bucket structure: public/expenditures/files/{filename}
      const fileKey = await s3Service.uploadDocument(file, {
        folder: "public/expenditures/files", // Match actual S3 bucket structure
        useLaravelStructure: true, // No timestamp prefix
        onProgress: (progress) => {
          setExpenses((prev) =>
            prev.map((expense, i) =>
              i === index
                ? { ...expense, fileProgress: progress }
                : expense
            )
          );
        },
      });

      // Update state with S3 key
      setExpenses((prev) =>
        prev.map((expense, i) =>
          i === index
            ? {
                ...expense,
                fileKey,
                fileUploading: false,
                fileProgress: 100,
              }
            : expense
        )
      );

      toast.success("File uploaded successfully!");
    } catch (error) {
      console.error("File upload error:", error);
      toast.error("File upload failed: " + error.message);

      // Reset file state on error
      setExpenses((prev) =>
        prev.map((expense, i) =>
          i === index
            ? {
                ...expense,
                file: null,
                fileKey: null,
                fileUploading: false,
                fileProgress: 0,
              }
            : expense
        )
      );
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    expenses.forEach((expense, index) => {
      if (!expense.invoice_number.trim()) {
        newErrors[`expenses.${index}.invoice_number`] =
          "Invoice number is required";
      }
      if (!expense.creditor.trim()) {
        newErrors[`expenses.${index}.creditor`] = "Company is required";
      }
      if (!expense.due_date) {
        newErrors[`expenses.${index}.due_date`] = "Due date is required";
      }
      if (!expense.amount || parseFloat(expense.amount) <= 0) {
        newErrors[`expenses.${index}.amount`] =
          "Amount due is required and must be greater than 0";
      }
      if (!expense.type_id) {
        newErrors[`expenses.${index}.type_id`] = "Type is required";
      }
      if (!expense.property_id) {
        newErrors[`expenses.${index}.property_id`] = "Property is required";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors before submitting");
      return;
    }

    setLoading(true);

    try {
      // Prepare expenses data for backend (Laravel format)
      const expensesData = expenses.map((expense) => ({
        ticket_id: expense.ticket_id || null,
        property_id: expense.property_id || null,
        type_id: expense.type_id || null,
        purchase_order_number: expense.purchase_order_number || "",
        invoice_number: expense.invoice_number,
        creditor: expense.creditor,
        due_date: expense.due_date,
        amount: expense.amount,
        fileKey: expense.fileKey || null, // Backend expects fileKey (S3 key), not file
      }));

      // Call real API with Laravel-compatible format
      const response = await expendituresService.createExpenses({
        expenses: expensesData,
      });

      if (response.success) {
        toast.success(response.message || "Expenses created successfully!");
        navigate("/finance/expense-account");
      } else {
        toast.error(response.message || "Failed to create expenses");
      }
    } catch (error) {
      console.error("Error creating expenses:", error);
      
      // Handle validation errors from backend
      if (error.response?.data?.errors) {
        const backendErrors = error.response.data.errors;
        backendErrors.forEach((errorMsg) => {
          toast.error(errorMsg);
        });
      } else {
        toast.error("Failed to create expenses");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate("/finance/expense-account");
  };

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <Link to="/dashboard">Dashboard</Link>
        <span className={styles.separator}>/</span>
        <Link to="/finance/expense-account">Finances</Link>
        <span className={styles.separator}>/</span>
        <span className={styles.currentPage}>Add many expenses</span>
      </div>

      <div className={styles.mainSection}>
        <div className={styles.row}>
          <div className={styles.colXs12}>
            <div className={styles.panelTitle}>
              <h3>Add many expenses</h3>
              <div className={styles.addNewLink}>
                <button
                  style={{
                    fontWeight: "bold",
                    color: "#417ABA",
                    fontsSize: "14px",
                    textDecoration: "none",
                  }}
                  type="button"
                  className={styles.addNewButton}
                  onClick={addExpense}
                >
                  Add new
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              {expenses.map((expense, index) => (
                <div key={index} className={styles.expensePanel}>
                  {/* Action Links */}
                  <div className={styles.actionLinks}>
                    <button
                      style={{
                        fontWeight: "bold",
                        color: "#FF0000",
                        fontsSize: "14px",
                        textDecoration: "none",
                      }}
                      type="button"
                      className={styles.deleteLink}
                      onClick={() => removeExpense(index)}
                    >
                      Delete
                    </button>
                    <button
                      style={{
                        fontWeight: "bold",
                        color: "#417ABA",
                        fontsSize: "14px",
                        textDecoration: "none",
                      }}
                      type="button"
                      className={styles.duplicateLink}
                      onClick={() => duplicateExpense(index)}
                    >
                      Duplicate
                    </button>
                  </div>

                  {/* Form Fields in 3 Columns */}
                  <div className={styles.formFields}>
                    {/* Left Column */}
                    <div className={styles.column}>
                      <div className={styles.formGroup}>
                        <label className={styles.controlLabel}>Ticket Id</label>
                        <input
                          type="text"
                          className={styles.formControl}
                          value={expense.ticket_id}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "ticket_id",
                              e.target.value
                            )
                          }
                        />
                        {errors[`expenses.${index}.ticket_id`] && (
                          <div className={styles.errorMessage}>
                            {errors[`expenses.${index}.ticket_id`]}
                          </div>
                        )}
                      </div>

                      <div className={styles.formGroup}>
                        <label className={styles.controlLabel}>
                          Purchase Order
                        </label>
                        <input
                          type="text"
                          className={styles.formControl}
                          value={expense.purchase_order_number}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "purchase_order_number",
                              e.target.value
                            )
                          }
                        />
                        {errors[`expenses.${index}.purchase_order_number`] && (
                          <div className={styles.errorMessage}>
                            {errors[`expenses.${index}.purchase_order_number`]}
                          </div>
                        )}
                      </div>

                      <div className={styles.formGroup}>
                        <label
                          className={`${styles.controlLabel} ${styles.labelRequired}`}
                        >
                          Due Date
                        </label>
                        <input
                          type="date"
                          className={styles.formControl}
                          required
                          value={expense.due_date}
                          onChange={(e) =>
                            handleInputChange(index, "due_date", e.target.value)
                          }
                        />
                        {errors[`expenses.${index}.due_date`] && (
                          <div className={styles.errorMessage}>
                            {errors[`expenses.${index}.due_date`]}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Middle Column */}
                    <div className={styles.column}>
                      <div className={styles.formGroup}>
                        <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Property</label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="text"
                            className={styles.formControl}
                            required
                            value={expense.property_name}
                            onChange={(e) => {
                              handleInputChange(
                                index,
                                "property_name",
                                e.target.value
                              );
                              handlePropertySearch(e);
                            }}
                            placeholder="Type property ID or name (or *)"
                            style={{ border: '1px solid #0a7280' }}
                          />
                          {showPropertyDropdown && properties.length > 0 && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              backgroundColor: 'white',
                              border: '1px solid #ccc',
                              borderTop: 'none',
                              maxHeight: '200px',
                              overflowY: 'auto',
                              zIndex: 1000,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}>
                              {properties.map((property) => (
                                <div
                                  key={property.id}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #eee'
                                  }}
                                  onClick={() => handlePropertySelect(property, index)}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                >
                                  {property.name} ({property.property_id || property.id})
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {errors[`expenses.${index}.property`] && (
                          <div className={styles.errorMessage}>
                            {errors[`expenses.${index}.property`]}
                          </div>
                        )}
                      </div>

                      <div className={styles.formGroup}>
                        <label
                          className={`${styles.controlLabel} ${styles.labelRequired}`}
                        >
                          Invoice number
                        </label>
                        <input
                          type="text"
                          className={styles.formControl}
                          required
                          value={expense.invoice_number}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "invoice_number",
                              e.target.value
                            )
                          }
                        />
                        {errors[`expenses.${index}.invoice_number`] && (
                          <div className={styles.errorMessage}>
                            {errors[`expenses.${index}.invoice_number`]}
                          </div>
                        )}
                      </div>

                      <div className={styles.formGroup}>
                        <label
                          className={`${styles.controlLabel} ${styles.labelRequired}`}
                        >
                          Amount Due
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          className={styles.formControl}
                          required
                          value={expense.amount}
                          onChange={(e) =>
                            handleInputChange(index, "amount", e.target.value)
                          }
                        />
                        {errors[`expenses.${index}.amount`] && (
                          <div className={styles.errorMessage}>
                            {errors[`expenses.${index}.amount`]}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className={styles.column}>
                      <div className={styles.formGroup}>
                        <label
                          className={`${styles.controlLabel} ${styles.labelRequired}`}
                        >
                          Type
                        </label>
                        <select
                          className={styles.formControl}
                          required
                          value={expense.type_id}
                          onChange={(e) =>
                            handleInputChange(index, "type_id", e.target.value)
                          }
                        >
                          <option value="">Please Select</option>
                          {types.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.name}
                            </option>
                          ))}
                        </select>
                        {errors[`expenses.${index}.type_id`] && (
                          <div className={styles.errorMessage}>
                            {errors[`expenses.${index}.type_id`]}
                          </div>
                        )}
                      </div>

                      <div className={styles.formGroup}>
                        <label
                          className={`${styles.controlLabel} ${styles.labelRequired}`}
                        >
                          Company
                        </label>
                        <input
                          type="text"
                          className={styles.formControl}
                          required
                          value={expense.creditor}
                          onChange={(e) =>
                            handleInputChange(index, "creditor", e.target.value)
                          }
                        />
                        {errors[`expenses.${index}.creditor`] && (
                          <div className={styles.errorMessage}>
                            {errors[`expenses.${index}.creditor`]}
                          </div>
                        )}
                      </div>

                      <div className={styles.formGroup}>
                        <label className={styles.controlLabel}>File</label>
                        <input
                          type="file"
                          className={styles.formControl}
                          onChange={(e) =>
                            handleFileChange(index, e.target.files[0])
                          }
                          disabled={expense.fileUploading}
                        />
                        {expense.fileUploading && (
                          <div className={styles.uploadProgress}>
                            <div className={styles.progressBar}>
                              <div 
                                className={styles.progressFill} 
                                style={{ width: `${expense.fileProgress}%` }}
                              ></div>
                            </div>
                            <span className={styles.progressText}>
                              Uploading... {Math.round(expense.fileProgress)}%
                            </span>
                          </div>
                        )}
                        {expense.fileKey && !expense.fileUploading && (
                          <div className={styles.uploadSuccess}>
                            <span className={styles.successText}>✓ File uploaded successfully</span>
                          </div>
                        )}
                        {errors[`expenses.${index}.file`] && (
                          <div className={styles.errorMessage}>
                            {errors[`expenses.${index}.file`]}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Save Button */}
              <div className={styles.saveButtonContainer}>
                <button
                  type="submit"
                  className={styles.saveButton}
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className={styles.cancelButton}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseAccountCreate;
